// Hook for managing user API keys
import { useState, useEffect, useCallback } from 'react';
import { 
  auth,
  getUserApiKeys, 
  createApiKey, 
  deleteApiKey, 
  toggleApiKey,
  type ApiKey 
} from '../lib/firebase';
import { useAuth } from './useAuth';

interface ApiKeysState {
  keys: ApiKey[];
  loading: boolean;
  error: string | null;
}

export function useApiKeys() {
  const { user } = useAuth();
  const [state, setState] = useState<ApiKeysState>({
    keys: [],
    loading: true,
    error: null,
  });

  // Fetch keys when user changes
  const fetchKeys = useCallback(async () => {
    if (!user) {
      setState({ keys: [], loading: false, error: null });
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const userKeys = await getUserApiKeys(user.uid);
      setState({ keys: userKeys, loading: false, error: null });
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load API keys',
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  // Create a new API key
  const create = useCallback(async (name: string): Promise<ApiKey> => {
    const uid = user?.uid || auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');

    setState(prev => ({ ...prev, error: null }));

    try {
      const newKey = await createApiKey(uid, name);
      setState(prev => ({
        ...prev,
        keys: [newKey, ...prev.keys],
      }));
      return newKey;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create API key';
      setState(prev => ({ ...prev, error: message }));
      throw new Error(message);
    }
  }, [user]);

  // Delete an API key
  const remove = useCallback(async (keyId: string): Promise<void> => {
    const uid = user?.uid || auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');

    setState(prev => ({ ...prev, error: null }));

    try {
      await deleteApiKey(uid, keyId);
      setState(prev => ({
        ...prev,
        keys: prev.keys.filter(k => k.id !== keyId),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete API key';
      setState(prev => ({ ...prev, error: message }));
      throw new Error(message);
    }
  }, [user]);

  // Toggle API key active status
  const toggle = useCallback(async (keyId: string, active: boolean): Promise<void> => {
    const uid = user?.uid || auth.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');

    setState(prev => ({ ...prev, error: null }));

    try {
      await toggleApiKey(uid, keyId, active);
      setState(prev => ({
        ...prev,
        keys: prev.keys.map(k => 
          k.id === keyId ? { ...k, active } : k
        ),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update API key';
      setState(prev => ({ ...prev, error: message }));
      throw new Error(message);
    }
  }, [user]);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    keys: state.keys,
    loading: state.loading,
    error: state.error,
    create,
    remove,
    toggle,
    refresh: fetchKeys,
    clearError,
  };
}
